/**
 * Sanitizace markdownu (spec 08 §2 — „MarkdownView (reporty, wiki — sanitizace!)").
 *
 * Reporty píše LLM a poznatky v HAW může editovat kdokoli — obojí je nedůvěryhodný
 * vstup. Markdown se proto renderuje a PAK protahuje DOMPurify; do DOM nesmí nic,
 * co neprošlo `sanitizeMarkdown()`. Hlídá to i eslint pravidlo
 * `no-restricted-syntax` v .eslintrc.cjs.
 */
import DOMPurify from 'dompurify';
import { marked } from 'marked';

marked.setOptions({
  gfm: true,
  breaks: false,
});

const ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'hr',
  'strong', 'em', 'del', 'code', 'pre', 'blockquote',
  'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'a', 'span', 'div',
];

const ALLOWED_ATTR = ['href', 'title', 'class', 'colspan', 'rowspan', 'align'];

/**
 * Externí odkazy dostanou rel="noopener noreferrer" a target="_blank".
 * Hook se registruje jednou; DOMPurify je singleton.
 */
let hookRegistered = false;
function ensureHook() {
  if (hookRegistered) return;
  DOMPurify.addHook('afterSanitizeAttributes', (node: Node) => {
    if (node instanceof HTMLAnchorElement && node.hasAttribute('href')) {
      const href = node.getAttribute('href') ?? '';
      // Interní odkazy (routy aplikace) nechat v tabu.
      if (/^https?:\/\//i.test(href)) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    }
  });
  hookRegistered = true;
}

/** Markdown → bezpečné HTML. Jediná cesta, kudy smí HTML do DOM. */
export function sanitizeMarkdown(markdown: string | null | undefined): string {
  if (!markdown) return '';
  ensureHook();
  const rendered = marked.parse(markdown, { async: false }) as string;
  return DOMPurify.sanitize(rendered, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // javascript:, data: apod. se zahodí
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });
}

/** Sanitizace holého textu (bez markdown renderu) — pro snippety z RAG. */
export function sanitizeText(text: string | null | undefined): string {
  if (!text) return '';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: ['mark', 'strong', 'em'], ALLOWED_ATTR: [] });
}
